import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const tabs = ["Our Story", "Quality Promise", "Contact Us"] as const;
type Tab = typeof tabs[number];

const stats = [
  { value: "2018", label: "Founded" },
  { value: "30+", label: "Products" },
  { value: "500+", label: "Happy Customers" },
  { value: "4.7★", label: "Avg. Rating" },
];

const values = [
  {
    icon: "ri-leaf-line",
    title: "Sustainability First",
    desc: "Every piece of wood we use is FSC-certified and sustainably sourced. We partner with responsible suppliers who share our commitment to protecting forests for future generations.",
  },
  {
    icon: "ri-hammer-line",
    title: "Artisan Craftsmanship",
    desc: "Our furniture is handcrafted by skilled artisans with decades of experience. Each joint, weld, and weave is inspected to ensure it meets the ARAVELLA standard of excellence.",
  },
  {
    icon: "ri-sun-line",
    title: "Built for the Southwest",
    desc: "Based in Phoenix, AZ, we design every product to withstand intense desert sun, monsoon rains, and extreme temperature swings — so your investment lasts for decades.",
  },
  {
    icon: "ri-heart-line",
    title: "Customer Obsessed",
    desc: "From your first browse to white-glove delivery and beyond, we are here for you. Our team in Phoenix is always a call or email away to make your experience exceptional.",
  },
];

const materials = [
  {
    img: "https://public.readdy.ai/ai/img_res/8eafa5a7536f86750efe3f7d1e6c0951.jpg",
    title: "Grade A Teak Wood",
    desc: "The gold standard of outdoor wood. Naturally rich in oils that repel water and resist rot, Grade A teak requires virtually no maintenance and develops a distinguished silver patina over time.",
    badge: "FSC Certified",
  },
  {
    img: "https://readdy.ai/api/search-image?query=close%20up%20of%20hand%20woven%20PE%20rattan%20wicker%20texture%2C%20tight%20weave%20pattern%2C%20all%20weather%20synthetic%20rattan%2C%20premium%20outdoor%20furniture%20material%20macro%20photography&width=400&height=300&seq=mat002&orientation=landscape",
    title: "All-Weather PE Rattan",
    desc: "Our PE rattan is engineered to resist UV fading, moisture, and temperature extremes. Hand-woven over powder-coated aluminum frames, it delivers the look of natural rattan with superior durability.",
    badge: "UV Resistant",
  },
  {
    img: "https://readdy.ai/api/search-image?query=close%20up%20of%20solid%20acacia%20wood%20surface%2C%20rich%20natural%20grain%20pattern%2C%20warm%20brown%20tones%2C%20premium%20hardwood%20outdoor%20furniture%20material%20detail%20photography&width=400&height=300&seq=mat003&orientation=landscape",
    title: "Solid Acacia Wood",
    desc: "Each acacia piece features a completely unique grain pattern — no two are alike. The dense hardwood is naturally water-resistant and oil-finished for lasting beauty in any outdoor setting.",
    badge: "Unique Grain",
  },
  {
    img: "https://readdy.ai/api/search-image?query=powder%20coated%20aluminum%20outdoor%20furniture%20frame%20detail%2C%20matte%20finish%2C%20rust%20proof%20coating%2C%20quality%20outdoor%20furniture%20construction%20engineering%20close%20up&width=400&height=300&seq=mat004&orientation=landscape",
    title: "Powder-Coated Aluminum",
    desc: "Our aluminum frames are powder-coated for maximum rust and corrosion resistance. Lightweight yet incredibly strong, they form the backbone of our wicker and rattan collections.",
    badge: "Rust-Proof",
  },
];

const team = [
  {
    img: "https://readdy.ai/api/search-image?query=professional%20woman%20portrait%20headshot%2C%20business%20casual%2C%20warm%20confident%20smile%2C%20natural%20light%2C%20clean%20background%2C%20company%20founder%20executive&width=200&height=200&seq=team001&orientation=squarish",
    name: "Maria Aravella",
    role: "Founder & CEO",
    bio: "With 15 years in luxury interior design, Maria founded ARAVELLA LLC to bring resort-quality outdoor furniture to Arizona homeowners at honest prices.",
  },
  {
    img: "https://readdy.ai/api/search-image?query=professional%20man%20portrait%20headshot%2C%20business%20casual%2C%20friendly%20confident%20smile%2C%20natural%20light%2C%20clean%20background%2C%20operations%20director%20executive&width=200&height=200&seq=team002&orientation=squarish",
    name: "James Thornton",
    role: "Head of Operations",
    bio: "James oversees our supply chain and quality control, ensuring every product that leaves our Phoenix warehouse meets the ARAVELLA standard.",
  },
  {
    img: "https://readdy.ai/api/search-image?query=professional%20woman%20portrait%20headshot%2C%20business%20casual%2C%20warm%20smile%2C%20natural%20light%2C%20clean%20background%2C%20design%20director%20creative&width=200&height=200&seq=team003&orientation=squarish",
    name: "Sofia Reyes",
    role: "Lead Designer",
    bio: "Sofia brings a background in sustainable architecture to every collection, balancing timeless aesthetics with practical outdoor performance.",
  },
];

export default function AboutPage() {
  const location = useLocation();
  const getInitialTab = (): Tab => {
    if (location.pathname.includes("quality")) return "Quality Promise";
    if (location.pathname.includes("contact")) return "Contact Us";
    return "Our Story";
  };
  const [activeTab, setActiveTab] = useState<Tab>(getInitialTab);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  useEffect(() => {
    setActiveTab(getInitialTab());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    setFormStatus("sending");
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch("https://readdy.ai/api/form/d7abrsabdfubkqslaj20", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[500px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20outdoor%20furniture%20showroom%20interior%20with%20teak%20wood%20pieces%20and%20wicker%20sofas%2C%20warm%20natural%20light%20through%20large%20windows%2C%20elegant%20retail%20space%2C%20premium%20furniture%20brand%20atmosphere%2C%20Phoenix%20Arizona&width=1440&height=500&seq=about-hero&orientation=landscape"
            alt="ARAVELLA LLC showroom"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <span className="inline-block text-[#A8D080] text-xs font-semibold tracking-widest uppercase mb-4 border border-[#A8D080]/40 px-4 py-1.5 rounded-full">
              Phoenix, Arizona — Est. 2018
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              About ARAVELLA LLC
            </h1>
            <p className="text-white/75 text-lg leading-relaxed max-w-xl">
              We are a Phoenix-based outdoor furniture company dedicated to bringing premium, sustainably crafted pieces to homes across the Southwest.
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="sticky top-[68px] lg:top-[104px] z-30 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-0">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? "border-b-2 border-[#2D5016] text-[#2D5016]"
                    : "text-gray-400 hover:text-gray-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── OUR STORY ── */}
      {activeTab === "Our Story" && (
        <main>
          {/* Stats */}
          <section className="bg-[#2D5016] py-12">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {stats.map(s => (
                  <div key={s.label} className="text-center">
                    <div className="text-4xl font-bold text-white mb-1">{s.value}</div>
                    <div className="text-[#A8D080] text-sm tracking-wide">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Story Section */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <span className="text-[#4A7C2F] text-xs font-semibold uppercase tracking-widest">Our Beginning</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mt-3 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Born from a Love of<br />Outdoor Living
                  </h2>
                  <div className="space-y-4 text-gray-500 text-sm leading-relaxed">
                    <p>
                      ARAVELLA LLC was founded in 2018 in Phoenix, Arizona, with a simple mission: to make resort-quality outdoor furniture accessible to every homeowner in the Southwest. Our founder, Maria Aravella, spent years sourcing premium pieces for luxury interior design projects and saw firsthand how transformative the right outdoor furniture could be.
                    </p>
                    <p>
                      Frustrated by the gap between cheap, disposable patio sets and prohibitively expensive designer pieces, she set out to build a brand that offered genuine quality at honest prices — furniture that would last decades, not seasons.
                    </p>
                    <p>
                      Today, ARAVELLA LLC operates from our Phoenix headquarters at <strong>2340 W Osborn Rd, Phoenix, AZ 85015</strong>, serving customers across Arizona and the contiguous United States. Every product in our catalog is personally selected and tested by our team to ensure it meets our exacting standards for materials, construction, and comfort.
                    </p>
                  </div>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <Link to="/products" className="px-6 py-3 bg-[#2D5016] text-white text-sm font-semibold rounded-full hover:bg-[#3a6b1e] transition-colors cursor-pointer whitespace-nowrap">
                      Shop Our Collection
                    </Link>
                    <button onClick={() => setActiveTab("Contact Us")} className="px-6 py-3 border border-[#2D5016] text-[#2D5016] text-sm font-semibold rounded-full hover:bg-[#f5f9f0] transition-colors cursor-pointer whitespace-nowrap">
                      Get in Touch
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <div className="w-full h-[420px] rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=beautiful%20outdoor%20patio%20furniture%20setup%20in%20Arizona%20desert%20backyard%2C%20teak%20wood%20lounge%20chairs%20and%20wicker%20sofa%2C%20lush%20landscaping%2C%20warm%20golden%20afternoon%20light%2C%20luxury%20outdoor%20living%20space%2C%20ARAVELLA%20brand%20lifestyle&width=600&height=420&seq=story001&orientation=landscape"
                      alt="ARAVELLA outdoor living"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 w-52">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 flex items-center justify-center bg-[#2D5016]/10 rounded-full">
                        <i className="ri-map-pin-line text-[#2D5016] text-xs"></i>
                      </span>
                      <span className="text-xs font-semibold text-[#1a1a1a]">Phoenix, AZ</span>
                    </div>
                    <p className="text-xs text-gray-400 leading-snug">2340 W Osborn Rd<br />Phoenix, AZ 85015</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-20 bg-[#F9F7F3]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <span className="text-[#4A7C2F] text-xs font-semibold uppercase tracking-widest">What We Stand For</span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Our Core Values
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {values.map(v => (
                  <div key={v.title} className="bg-white rounded-2xl p-8 flex gap-5">
                    <span className="w-12 h-12 flex items-center justify-center bg-[#2D5016]/10 rounded-xl flex-shrink-0 mt-0.5">
                      <i className={`${v.icon} text-[#2D5016] text-xl`}></i>
                    </span>
                    <div>
                      <h3 className="text-base font-bold text-[#1a1a1a] mb-2">{v.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Team */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <span className="text-[#4A7C2F] text-xs font-semibold uppercase tracking-widest">The People Behind ARAVELLA</span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Meet Our Team
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {team.map(member => (
                  <div key={member.name} className="text-center">
                    <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-5">
                      <img src={member.img} alt={member.name} className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-base font-bold text-[#1a1a1a] mb-0.5">{member.name}</h3>
                    <span className="text-xs text-[#4A7C2F] font-semibold uppercase tracking-wide">{member.role}</span>
                    <p className="text-sm text-gray-500 leading-relaxed mt-3 max-w-xs mx-auto">{member.bio}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Location Map */}
          <section className="py-20 bg-[#F9F7F3]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="text-[#4A7C2F] text-xs font-semibold uppercase tracking-widest">Find Us</span>
                  <h2 className="text-3xl font-bold text-[#1a1a1a] mt-3 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Visit Our Phoenix Showroom
                  </h2>
                  <div className="space-y-4 mb-8">
                    {[
                      { icon: "ri-map-pin-line", label: "Address", value: "2340 W Osborn Rd, Phoenix, AZ 85015" },
                      { icon: "ri-phone-line", label: "Phone", value: "(602) 555-1234" },
                      { icon: "ri-mail-line", label: "Email", value: "aravellallc@outlook.com" },
                      { icon: "ri-time-line", label: "Hours", value: "Mon–Sat 9am–6pm · Sun 11am–5pm" },
                    ].map(item => (
                      <div key={item.label} className="flex items-start gap-4">
                        <span className="w-10 h-10 flex items-center justify-center bg-[#2D5016]/10 rounded-xl flex-shrink-0">
                          <i className={`${item.icon} text-[#2D5016] text-base`}></i>
                        </span>
                        <div>
                          <div className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{item.label}</div>
                          <div className="text-sm font-medium text-[#1a1a1a]">{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setActiveTab("Contact Us")} className="px-6 py-3 bg-[#2D5016] text-white text-sm font-semibold rounded-full hover:bg-[#3a6b1e] transition-colors cursor-pointer whitespace-nowrap">
                    Send Us a Message
                  </button>
                </div>
                <div className="w-full h-[380px] rounded-2xl overflow-hidden">
                  <iframe
                    title="ARAVELLA LLC Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3328.5!2d-112.1!3d33.48!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872b12!2s2340+W+Osborn+Rd%2C+Phoenix%2C+AZ+85015!5e0!3m2!1sen!2sus!4v1"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ── QUALITY PROMISE ── */}
      {activeTab === "Quality Promise" && (
        <main>
          {/* Intro */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-6 text-center max-w-3xl mx-auto">
              <span className="text-[#4A7C2F] text-xs font-semibold uppercase tracking-widest">The ARAVELLA Standard</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mt-3 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Built to Last a Lifetime
              </h2>
              <p className="text-gray-500 text-base leading-relaxed">
                Every ARAVELLA product goes through a rigorous multi-stage quality process — from raw material selection to final inspection — before it reaches your home. We back every piece with our industry-leading 5-year warranty.
              </p>
            </div>
          </section>

          {/* Materials */}
          <section className="pb-20">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <span className="text-[#4A7C2F] text-xs font-semibold uppercase tracking-widest">Premium Materials</span>
                <h2 className="text-3xl font-bold text-[#1a1a1a] mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  What We Build With
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {materials.map(m => (
                  <div key={m.title} className="bg-[#F9F7F3] rounded-2xl overflow-hidden flex flex-col">
                    <div className="w-full h-52 overflow-hidden">
                      <img src={m.img} alt={m.title} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="p-6 flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-base font-bold text-[#1a1a1a]">{m.title}</h3>
                        <span className="text-xs bg-[#2D5016]/10 text-[#2D5016] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap">{m.badge}</span>
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Quality Process */}
          <section className="py-20 bg-[#F9F7F3]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-14">
                <span className="text-[#4A7C2F] text-xs font-semibold uppercase tracking-widest">Our Process</span>
                <h2 className="text-3xl font-bold text-[#1a1a1a] mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  From Forest to Your Patio
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  { step: "01", icon: "ri-leaf-line", title: "Responsible Sourcing", desc: "We partner exclusively with FSC-certified suppliers who practice sustainable forestry and ethical labor standards." },
                  { step: "02", icon: "ri-hammer-line", title: "Artisan Construction", desc: "Skilled craftspeople hand-assemble each piece, applying traditional joinery techniques alongside modern precision tools." },
                  { step: "03", icon: "ri-shield-check-line", title: "Rigorous Inspection", desc: "Every item undergoes a 27-point quality inspection covering structural integrity, finish quality, and hardware function." },
                  { step: "04", icon: "ri-truck-line", title: "White Glove Delivery", desc: "Our delivery team brings your furniture inside, assembles it, and removes all packaging — leaving you with a perfect setup." },
                ].map(p => (
                  <div key={p.step} className="bg-white rounded-2xl p-6 text-center">
                    <div className="text-4xl font-bold text-[#2D5016]/15 mb-3">{p.step}</div>
                    <span className="w-12 h-12 flex items-center justify-center bg-[#2D5016]/10 rounded-xl mx-auto mb-4">
                      <i className={`${p.icon} text-[#2D5016] text-xl`}></i>
                    </span>
                    <h3 className="text-sm font-bold text-[#1a1a1a] mb-2">{p.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Warranty */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-6">
              <div className="bg-[#2D5016] rounded-3xl p-10 md:p-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <span className="text-[#A8D080] text-xs font-semibold uppercase tracking-widest">Our Promise</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                    5-Year ARAVELLA Warranty
                  </h2>
                  <p className="text-white/75 text-sm leading-relaxed mb-6">
                    We stand behind every product we sell. If any structural defect occurs within 5 years of purchase, we will repair or replace it — no questions asked. That is the ARAVELLA promise.
                  </p>
                  <ul className="space-y-3">
                    {[
                      "Covers all structural defects",
                      "Includes frame, joints & hardware",
                      "Transferable to new homeowners",
                      "No-hassle claim process",
                    ].map(item => (
                      <li key={item} className="flex items-center gap-2.5 text-sm text-white/80">
                        <span className="w-5 h-5 flex items-center justify-center bg-[#A8D080]/20 rounded-full flex-shrink-0">
                          <i className="ri-check-line text-[#A8D080] text-xs"></i>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="w-full h-64 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=premium%20outdoor%20teak%20furniture%20on%20luxury%20patio%2C%20beautiful%20Arizona%20desert%20landscape%20background%2C%20warm%20golden%20sunset%20light%2C%20ARAVELLA%20quality%20craftsmanship%2C%20long%20lasting%20outdoor%20furniture%20lifestyle&width=500&height=300&seq=warranty001&orientation=landscape"
                    alt="ARAVELLA warranty"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>
        </main>
      )}

      {/* ── CONTACT US ── */}
      {activeTab === "Contact Us" && (
        <main className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="text-[#4A7C2F] text-xs font-semibold uppercase tracking-widest">We&apos;d Love to Hear From You</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Get in Touch
              </h2>
              <p className="text-gray-500 text-sm mt-3 max-w-md mx-auto">
                Questions about a product, your order, or our warranty? Our Phoenix team is ready to help.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Contact Info */}
              <div className="space-y-5">
                {[
                  { icon: "ri-map-pin-line", title: "Our Address", lines: ["2340 W Osborn Rd", "Phoenix, AZ 85015"] },
                  { icon: "ri-phone-line", title: "Phone", lines: ["(602) 555-1234"] },
                  { icon: "ri-mail-line", title: "Email", lines: ["aravellallc@outlook.com"] },
                  { icon: "ri-time-line", title: "Business Hours", lines: ["Mon–Sat: 9:00 AM – 6:00 PM", "Sunday: 11:00 AM – 5:00 PM"] },
                ].map(item => (
                  <div key={item.title} className="flex gap-4 bg-[#F9F7F3] rounded-2xl p-5">
                    <span className="w-10 h-10 flex items-center justify-center bg-[#2D5016]/10 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-[#2D5016] text-base`}></i>
                    </span>
                    <div>
                      <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{item.title}</div>
                      {item.lines.map(line => (
                        <div key={line} className="text-sm font-medium text-[#1a1a1a]">{line}</div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Social */}
                <div className="bg-[#F9F7F3] rounded-2xl p-5">
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-3">Follow Us</div>
                  <div className="flex gap-3">
                    {[
                      { icon: "ri-instagram-line", label: "Instagram" },
                      { icon: "ri-facebook-line", label: "Facebook" },
                      { icon: "ri-pinterest-line", label: "Pinterest" },
                    ].map(s => (
                      <a key={s.label} href="https://instagram.com" target="_blank" rel="nofollow noopener noreferrer"
                        className="w-10 h-10 flex items-center justify-center bg-white rounded-xl text-[#2D5016] hover:bg-[#2D5016] hover:text-white transition-colors cursor-pointer">
                        <i className={`${s.icon} text-base`}></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <div className="bg-[#F9F7F3] rounded-2xl p-8">
                  {formStatus === "success" ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <span className="w-16 h-16 flex items-center justify-center bg-[#2D5016]/10 rounded-full mb-5">
                        <i className="ri-check-line text-[#2D5016] text-3xl"></i>
                      </span>
                      <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">Message Sent!</h3>
                      <p className="text-gray-500 text-sm max-w-xs">Thank you for reaching out. Our Phoenix team will get back to you within 1 business day.</p>
                      <button onClick={() => setFormStatus("idle")} className="mt-6 px-6 py-2.5 bg-[#2D5016] text-white text-sm font-semibold rounded-full cursor-pointer whitespace-nowrap">
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <form data-readdy-form onSubmit={handleFormSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5 uppercase tracking-wide">Full Name *</label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleFormChange}
                            required
                            placeholder="Your full name"
                            className="w-full h-11 px-4 text-sm border border-gray-200 rounded-xl bg-white text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#2D5016] transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5 uppercase tracking-wide">Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            required
                            placeholder="your@email.com"
                            className="w-full h-11 px-4 text-sm border border-gray-200 rounded-xl bg-white text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#2D5016] transition-colors"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5 uppercase tracking-wide">Phone Number</label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleFormChange}
                            placeholder="(602) 000-0000"
                            className="w-full h-11 px-4 text-sm border border-gray-200 rounded-xl bg-white text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#2D5016] transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5 uppercase tracking-wide">Subject</label>
                          <select
                            name="subject"
                            value={formData.subject}
                            onChange={handleFormChange}
                            className="w-full h-11 px-4 text-sm border border-gray-200 rounded-xl bg-white text-[#1a1a1a] focus:outline-none focus:border-[#2D5016] transition-colors cursor-pointer"
                          >
                            <option value="">Select a topic</option>
                            <option value="Product Inquiry">Product Inquiry</option>
                            <option value="Order Status">Order Status</option>
                            <option value="Warranty Claim">Warranty Claim</option>
                            <option value="Delivery Question">Delivery Question</option>
                            <option value="Return & Refund">Return &amp; Refund</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5 uppercase tracking-wide">Message *</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleFormChange}
                          required
                          maxLength={500}
                          rows={5}
                          placeholder="Tell us how we can help you..."
                          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl bg-white text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#2D5016] transition-colors resize-none"
                        />
                        <div className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</div>
                      </div>
                      {formStatus === "error" && (
                        <p className="text-red-500 text-xs">Something went wrong. Please try again or email us directly.</p>
                      )}
                      <button
                        type="submit"
                        disabled={formStatus === "sending"}
                        className="w-full h-12 bg-[#2D5016] hover:bg-[#3a6b1e] text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                      >
                        {formStatus === "sending" ? "Sending..." : "Send Message"}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      )}

      <Footer />
    </div>
  );
}
